<script lang="ts">
import "../app.css";
import type { Snippet } from "svelte";
import { afterNavigate } from "$app/navigation";
import { page } from "$app/state";
import { countsAsNavigation, currentPath } from "$lib/consent/navigation";
import { identifyForAnalytics } from "$lib/consent/track";
import LanguageSwitcher from "$lib/i18n/LanguageSwitcher.svelte";
import { LOCALE_INFO, splitLocale } from "$lib/i18n/locales";
import LegalFooter from "$lib/nav/LegalFooter.svelte";
import MaintenancePage from "$lib/nav/MaintenancePage.svelte";
import ServiceNotice from "$lib/nav/ServiceNotice.svelte";
import TopNav from "$lib/nav/TopNav.svelte";
import {
  maintenancePageFor,
  type ServiceNotice as Notice,
  OPEN_NOTICE,
  readNotice,
  readOnlyNoticeFor,
} from "$lib/notice";
import type { LayoutData } from "./$types";

let { children, data }: { children: Snippet; data: LayoutData } = $props();

// The administrator console brings its own bar, deliberately unlike this one:
// the hazard of that section is doing something believing you are somewhere
// else, and two headers stacked — one of them the ordinary product's — would be
// the opposite of telling them apart.
const inConsole = $derived(page.url.pathname.startsWith("/admin"));

// Whether the service is closed, and the notice above the page. On a
// server-rendered address both come from the layout's own load, so they are in
// the HTML before any script runs — which is the whole point: an incident is
// when a client bundle is least trustworthy.
//
// A PRERENDERED page (the landing, the country pages, the free tools) was built
// before the notice existed and carries `null`. There the browser asks for it
// after hydration, the way the signed-in header is settled on those same pages.
// Without a script such a page shows no notice, which is inherent to a file
// written before the notice was published.
let fetched = $state.raw<Notice | null>(null);

$effect(() => {
  if (data.notice !== null) return;
  let live = true;
  fetch("/api/notice")
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => {
      if (live && body) fetched = readNotice(body);
    })
    .catch(() => {
      // A notice is not worth a broken page; the site reads exactly as it did.
    });
  return () => {
    live = false;
  };
});

// One page view per move the client router makes, handed to the consent
// layer's own bundle — which is outside this app, loads only after the
// measurement category is granted, and ignores the call when it is not. The
// first render is NOT counted here: that one is the page the browser loaded,
// and the bundle sends it itself.
// Who is signed in, as the page analytics may know it. Stated before the
// navigation's page view below, so a first view from the cabinet already
// carries it; a page built ahead of time has no request and states nobody.
$effect(() => {
  if (data.analyticsIdentity) identifyForAnalytics(data.analyticsIdentity);
});

afterNavigate((navigation) => {
  if (!countsAsNavigation(navigation.type)) return;
  window.__dcAnalytics?.pageview(currentPath(location));
});

// The document's language and writing direction follow the page. The server
// writes both into the `<html>` tag of every document it renders
// (hooks.server.ts); this keeps them true after a move the client router makes
// without a new document, such as from a translated page to an English-only
// one.
const locale = $derived(splitLocale(page.url.pathname).locale);
$effect(() => {
  const { lang, dir } = LOCALE_INFO[locale];
  document.documentElement.lang = lang;
  if (dir === "rtl") document.documentElement.dir = "rtl";
  else document.documentElement.removeAttribute("dir");
});

// The cabinet and the console are English-only working areas, so the switcher
// is offered on the public pages alone.
const inAccountArea = $derived(
  page.url.pathname.startsWith("/app") || page.url.pathname.startsWith("/admin"),
);

const notice = $derived(data.notice ?? fetched ?? OPEN_NOTICE);
const closed = $derived(maintenancePageFor(notice.mode, page.url.pathname));
const readOnly = $derived(readOnlyNoticeFor(notice.mode, page.url.pathname));
</script>

<div class="flex min-h-viewport flex-col bg-slate-50 text-slate-900">
  {#if !inConsole}
    <ServiceNotice announcement={notice.announcement} {readOnly} />
    <TopNav
      signedIn={data.signedIn}
      accountConfirmation={data.accountConfirmation}
      reopenDeleteDialog={data.reopenDeleteDialog}
    />
  {/if}
  <div class="flex-1">
    {#if closed}
      <MaintenancePage announcement={notice.announcement} />
    {:else}
      {@render children()}
    {/if}
  </div>
  <LegalFooter />
  {#if !inAccountArea}
    <LanguageSwitcher />
  {/if}
</div>
